import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone, MessageCircle } from "lucide-react";
import { NAV_LINKS, SITE } from "../data/site";
import { Logo } from "./Logo";

const Navbar = () => {
    const [open, setOpen] = useState(false);
    const location = useLocation();
    const hi = location.pathname.startsWith("/hi");
    const HI_MAP = { "/": "/hi", "/about": "/hi/about", "/services": "/hi/services", "/why-albl": "/hi/why-albl", "/markets": "/hi/markets", "/insights": "/hi/insights", "/contact": "/hi/contact" };
    const target = (to) => (hi ? HI_MAP[to] || to : to);

    const linkClass = ({ isActive }) =>
        `link-underline text-sm font-medium tracking-wide transition-colors duration-200 pb-1 ${
            isActive ? "text-[#0D131F] active" : "text-slate-500 hover:text-[#0D131F]"
        }`;

    return (
        <>
        <header className="fixed top-0 inset-x-0 z-50 bg-white/90 backdrop-blur-md border-b border-slate-200">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 h-16 sm:h-20 flex items-center justify-between">
                <Link
                    to={target("/")}
                    className="group"
                    data-testid="nav-logo"
                    aria-label="ALBL Communications — home"
                >
                    <Logo />
                </Link>

                <nav className="hidden lg:flex items-center gap-7" aria-label="Primary">
                    {NAV_LINKS.map((l) => (
                        <NavLink
                            key={l.to}
                            to={target(l.to)}
                            end={l.to === "/"}
                            className={linkClass}
                            data-testid={l.testId}
                        >
                            {l.label}
                        </NavLink>
                    ))}
                </nav>

                <div className="hidden lg:flex items-center gap-4">
                    <Link
                        to={location.pathname.startsWith("/hi") ? "/" : "/hi"}
                        data-testid="nav-lang-toggle"
                        className="btn-sharp flex items-center gap-1.5 text-xs font-bold tracking-widest border border-slate-300 px-4 py-2 text-slate-700 hover:border-[#0D131F] hover:bg-[#0D131F] hover:text-white"
                    >
                        {location.pathname.startsWith("/hi") ? "English" : "हिन्दी में देखें"}
                    </Link>
                    <a
                        href={SITE.whatsappPrefill}
                        target="_blank"
                        rel="noreferrer"
                        data-testid="nav-whatsapp-quick-link"
                        className="text-slate-500 hover:text-[#25D366] transition-colors"
                        aria-label="Chat with ALBL on WhatsApp"
                    >
                        <MessageCircle className="h-5 w-5" />
                    </a>
                    <Link
                        to={target("/contact") + "?intent=audit"}
                        data-testid="nav-cta-audit-button"
                        className="btn-sharp bg-[#0D131F] text-white text-xs font-semibold tracking-[0.15em] uppercase px-5 py-3"
                    >
                        Get a Growth Audit
                    </Link>
                </div>

                <Link
                    to={location.pathname.startsWith("/hi") ? "/" : "/hi"}
                    data-testid="nav-lang-toggle-mobile"
                    className="lg:hidden text-xs font-bold tracking-widest border border-slate-300 px-3 py-2 text-slate-700"
                >
                    {location.pathname.startsWith("/hi") ? "EN" : "हिन्दी"}
                </Link>
                <button
                    className="lg:hidden text-[#0D131F] p-2"
                    onClick={() => setOpen(true)}
                    data-testid="nav-mobile-menu-button"
                    aria-label="Open menu"
                >
                    <Menu className="h-6 w-6" />
                </button>
            </div>

        </header>

            {open && (
                <div
                    className="fixed inset-0 z-50 bg-[#0D131F] text-white flex flex-col"
                    data-testid="nav-mobile-drawer"
                >
                    <div className="flex items-center justify-between px-4 h-16 border-b border-white/10">
                        <Logo dark />
                        <button
                            onClick={() => setOpen(false)}
                            data-testid="nav-mobile-close-button"
                            aria-label="Close menu"
                            className="p-2"
                        >
                            <X className="h-6 w-6" />
                        </button>
                    </div>
                    <nav className="flex-1 flex flex-col gap-1 px-6 py-8 overflow-y-auto" aria-label="Mobile">
                        {NAV_LINKS.map((l, i) => (
                            <NavLink
                                key={l.to}
                                to={target(l.to)}
                                end={l.to === "/"}
                                onClick={() => setOpen(false)}
                                data-testid={`mobile-${l.testId}`}
                                className={({ isActive }) =>
                                    `font-display text-2xl font-semibold py-3 border-b border-white/5 transition-colors ${
                                        isActive || location.pathname === l.to
                                            ? "text-[#C5A059]"
                                            : "text-white hover:text-[#C5A059]"
                                    }`
                                }
                            >
                                <span className="text-xs text-slate-500 mr-3 font-body">0{i + 1}</span>
                                {l.label}
                            </NavLink>
                        ))}
                    </nav>
                    <div className="px-6 pb-8 grid grid-cols-2 gap-3">
                        <a
                            href={SITE.phoneHref}
                            data-testid="mobile-call-albl-button"
                            className="btn-sharp flex items-center justify-center gap-2 border border-white/25 text-white text-xs font-semibold tracking-[0.15em] uppercase py-4"
                        >
                            <Phone className="h-4 w-4" /> Call ALBL
                        </a>
                        <a
                            href={SITE.whatsappPrefill}
                            target="_blank"
                            rel="noreferrer"
                            data-testid="mobile-whatsapp-albl-button"
                            className="btn-sharp flex items-center justify-center gap-2 bg-[#25D366] text-[#0D131F] text-xs font-semibold tracking-[0.15em] uppercase py-4"
                        >
                            <MessageCircle className="h-4 w-4" /> WhatsApp ALBL
                        </a>
                        <Link
                            to={target("/contact") + "?intent=audit"}
                            onClick={() => setOpen(false)}
                            data-testid="mobile-cta-audit-button"
                            className="btn-sharp col-span-2 text-center bg-[#C5A059] text-[#0D131F] text-xs font-semibold tracking-[0.15em] uppercase py-4"
                        >
                            Get a Growth Audit
                        </Link>
                        <Link
                            to={location.pathname.startsWith("/hi") ? "/" : "/hi"}
                            onClick={() => setOpen(false)}
                            data-testid="mobile-lang-toggle"
                            className="btn-sharp col-span-2 text-center border border-white/25 text-slate-300 text-xs font-semibold tracking-[0.15em] uppercase py-3"
                        >
                            {location.pathname.startsWith("/hi") ? "English Website" : "हिन्दी में देखें"}
                        </Link>
                    </div>
                </div>
            )}
        </>
    );
};

export default Navbar;
